import React, { useContext, useEffect } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/modern/components/ui/dialog';
import { Button } from '@/modern/components/ui/button';
import { Label } from '@/modern/components/ui/label';
import { DatePicker } from '@/modern/components/ui/date-picker';
import { Input } from '@/modern/components/ui/input';
import { Combobox } from '@/modern/components/ui/combobox';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { format, differenceInHours } from 'date-fns';
import BryntumContext from '@/react/context/bryntumContext';
import { DateHelper } from '@bryntum/schedulerpro';
import { GRANTS } from '@/other/constants/PrivilegeTypes';
import { Clock } from 'lucide-react';

// ============================================================================
// TYPE DEFINITIONS & SCHEMA
// ============================================================================

const scheduleSchema = z.object({
  resourceId: z.string().min(1, 'Resource is required'),
  startDate: z.date(),
  startTime: z.string().min(1, 'Start time is required'),
  endDate: z.date(),
  endTime: z.string().min(1, 'End time is required'),
}).refine((data) => {
  const startDateTime = new Date(data.startDate);
  const [startHours, startMinutes] = data.startTime.split(':');
  startDateTime.setHours(parseInt(startHours), parseInt(startMinutes), 0, 0);

  const endDateTime = new Date(data.endDate);
  const [endHours, endMinutes] = data.endTime.split(':');
  endDateTime.setHours(parseInt(endHours), parseInt(endMinutes), 0, 0);

  return endDateTime > startDateTime;
}, {
  message: 'End date/time must be after start date/time',
  path: ['endDate'],
});

type ScheduleFormData = z.infer<typeof scheduleSchema>;

interface ScheduleEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  eventId?: string;
  taskId: string;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ScheduleEditModal: React.FC<ScheduleEditModalProps> = ({
  open,
  onOpenChange,
  mode,
  eventId,
  taskId
}) => {
  const { project, user } = useContext(BryntumContext);
  const [isLoading, setIsLoading] = React.useState(false);
  const [resources, setResources] = React.useState<Array<{ id: string; name: string }>>([]);

  // Check permissions
  const hasPermission = user?.can(GRANTS.grant_three);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors }
  } = useForm<ScheduleFormData>({
    resolver: zodResolver(scheduleSchema),
    defaultValues: {
      resourceId: '',
      startDate: DateHelper.floor(new Date(), '1 day'),
      startTime: '08:00',
      endDate: DateHelper.add(DateHelper.floor(new Date(), '1 day'), 1, 'day'),
      endTime: '17:00',
    }
  });

  const watchedStartDate = watch('startDate');
  const watchedStartTime = watch('startTime');
  const watchedEndDate = watch('endDate');
  const watchedEndTime = watch('endTime');

  // Calculate duration
  const calculateDuration = () => {
    if (!watchedStartDate || !watchedStartTime || !watchedEndDate || !watchedEndTime) {
      return 'N/A';
    }

    const startDateTime = new Date(watchedStartDate);
    const [startHours, startMinutes] = watchedStartTime.split(':');
    startDateTime.setHours(parseInt(startHours), parseInt(startMinutes), 0, 0);

    const endDateTime = new Date(watchedEndDate);
    const [endHours, endMinutes] = watchedEndTime.split(':');
    endDateTime.setHours(parseInt(endHours), parseInt(endMinutes), 0, 0);

    const hours = differenceInHours(endDateTime, startDateTime);
    return `${hours}h`;
  };

  // Load resources
  useEffect(() => {
    if (open && project) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const resourceStore = (project as any).resourceStore;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const resourceList = resourceStore.query(() => true, false).map((r: any) => ({
        id: String(r.id),
        name: r.name || 'Unnamed Resource'
      }));
      setResources(resourceList);
    }
  }, [open, project]);

  // Load existing event data in edit mode
  useEffect(() => {
    if (open && mode === 'edit' && eventId && project) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const eventStore = (project as any).eventStore;
      const event = eventStore?.getById(eventId);

      if (event) {
        const startDate = new Date(event.startDate);
        const endDate = new Date(event.endDate);

        // Get resource ID from assignment
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const assignmentStore = (project as any).assignmentStore;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const assignment = assignmentStore.query((a: any) => a.eventId === eventId, false)[0];
        const resourceId = assignment?.resourceId || event.resource?.id || '';

        reset({
          resourceId: String(resourceId),
          startDate: startDate,
          startTime: format(startDate, 'HH:mm'),
          endDate: endDate,
          endTime: format(endDate, 'HH:mm'),
        });
      }
    } else if (open && mode === 'create') {
      // Reset to defaults for create mode
      const startDate = DateHelper.floor(new Date(), '1 day');
      const endDate = DateHelper.add(startDate, 1, 'day');
      reset({
        resourceId: '',
        startDate: startDate,
        startTime: '08:00',
        endDate: endDate,
        endTime: '17:00',
      });
    }
  }, [open, mode, eventId, project, reset]);

  const onSubmit = async (data: ScheduleFormData) => {
    if (!project || !hasPermission) return;

    try {
      setIsLoading(true);

      // Combine date and time for start and end
      const startDateTime = new Date(data.startDate);
      const [startHours, startMinutes] = data.startTime.split(':');
      startDateTime.setHours(parseInt(startHours), parseInt(startMinutes), 0, 0);

      const endDateTime = new Date(data.endDate);
      const [endHours, endMinutes] = data.endTime.split(':');
      endDateTime.setHours(parseInt(endHours), parseInt(endMinutes), 0, 0);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const eventStore = (project as any).eventStore;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const assignmentStore = (project as any).assignmentStore;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const resourceStore = (project as any).resourceStore;

      const selectedResource = resourceStore.getById(data.resourceId);

      if (!selectedResource) {
        alert('Selected resource not found');
        setIsLoading(false);
        return;
      }

      if (mode === 'edit' && eventId) {
        // Update existing event
        const event = eventStore.getById(eventId);
        if (event) {
          event.set({
            startDate: startDateTime,
            endDate: endDateTime,
          });

          // Update assignment if resource changed
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const assignment = assignmentStore.query((a: any) => a.eventId === eventId, false)[0];
          if (assignment && assignment.resourceId !== data.resourceId) {
            assignment.set({
              resourceId: data.resourceId
            });
          }
        }
      } else {
        // Create new event
        const newEvent = eventStore.createRecord({
          taskId: taskId,
          startDate: startDateTime,
          endDate: endDateTime,
          swivuIsAddedByTaskHistoryTab: true,
        });

        eventStore.add(newEvent);

        // Create assignment to link resource to event
        const newAssignment = assignmentStore.createRecord({
          eventId: newEvent.id,
          resourceId: data.resourceId,
        });

        assignmentStore.add(newAssignment);
      }

      onOpenChange(false);
    } catch (error) {
      console.error('Error saving schedule:', error);
      alert('Failed to save schedule. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onOpenChange(false);
    }
  };

  if (!hasPermission) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-base font-normal">Permission Denied</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-4">
            You do not have permission to create or edit schedules.
          </p>
          <DialogFooter>
            <Button onClick={() => onOpenChange(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-[600px] max-h-[85vh] flex flex-col p-0 shadow-none">
        <DialogHeader className="px-6 py-6 flex-shrink-0 bg-background border-b">
          <DialogTitle className="text-base font-normal">
            {mode === 'create' ? 'Add Schedule Date' : 'Edit Schedule Date'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
          <div className="overflow-y-auto px-6 space-y-6 flex-1" style={{ paddingTop: '1.5rem', paddingBottom: '1.5rem' }}>
          {/* Schedule Details Section */}
          <div className="space-y-4">
          {/* Resource Selection */}
          <div className="space-y-2">
            <Label htmlFor="resourceId" className="text-sm font-medium">Resource *</Label>
            <Controller
              name="resourceId"
              control={control}
              render={({ field }) => (
                <Combobox
                  options={resources.map((resource) => ({
                    label: resource.name,
                    value: resource.id,
                  }))}
                  value={field.value}
                  onValueChange={(value) => field.onChange(value as string)}
                  placeholder="Select a resource"
                  emptyMessage="No resources found"
                  className="w-full"
                />
              )}
            />
            {errors.resourceId && (
              <p className="text-sm text-destructive">{errors.resourceId.message}</p>
            )}
          </div>

          {/* Start Date & Time - Two columns */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Start Date *</Label>
              <Controller
                name="startDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select start date"
                    error={errors.startDate?.message}
                  />
                )}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="startTime" className="text-sm font-medium">Start Time *</Label>
              <Controller
                name="startTime"
                control={control}
                render={({ field }) => (
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                    <Input
                      id="startTime"
                      type="time"
                      inputSize="sm"
                      autoComplete="off"
                      {...field}
                      className={`pl-10 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-3 [&::-webkit-calendar-picker-indicator]:w-5 [&::-webkit-calendar-picker-indicator]:h-5 [&::-webkit-calendar-picker-indicator]:cursor-pointer ${errors.startTime ? 'border-destructive' : ''}`}
                    />
                  </div>
                )}
              />
              {errors.startTime && (
                <p className="text-sm text-destructive">{errors.startTime.message}</p>
              )}
            </div>
          </div>

          {/* End Date & Time - Two columns */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">End Date *</Label>
              <Controller
                name="endDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select end date"
                    error={errors.endDate?.message}
                  />
                )}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="endTime" className="text-sm font-medium">End Time *</Label>
              <Controller
                name="endTime"
                control={control}
                render={({ field }) => (
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                    <Input
                      id="endTime"
                      type="time"
                      autoComplete="off"
                      inputSize="sm"
                      {...field}
                      className={`pl-10 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-3 [&::-webkit-calendar-picker-indicator]:w-5 [&::-webkit-calendar-picker-indicator]:h-5 [&::-webkit-calendar-picker-indicator]:cursor-pointer ${errors.endTime ? 'border-destructive' : ''}`}
                    />
                  </div>
                )}
              />
              {errors.endTime && (
                <p className="text-sm text-destructive">{errors.endTime.message}</p>
              )}
            </div>
          </div>

          {/* Duration (Read-only) */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Duration</Label>
            <div className="flex h-10 w-full rounded-md border border-input bg-muted px-3 py-2 text-sm">
              {calculateDuration()}
            </div>
          </div>
          </div>
          </div>

          <DialogFooter className="gap-2 px-6 py-4 flex-shrink-0 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleEditModal;
